import type { CatalogCup } from '@/domain/catalog/cup';
import { type CatalogLiner, type LinerSize, LinerType } from '@/domain/catalog/liner';
import {
    type CatalogHead,
    type HeadOffset,
    HeadSize,
    headSizeFromString,
    type HeadSizeString,
    headSizeString,
    type HeadSystem,
} from '@/domain/catalog/head';
import type { CatalogStem } from '@/domain/catalog/stem';
import type { Url } from '@/domain/types';
import { filter } from 'ramda';
import { BearingInnerDiameter, type CatalogBearing } from '@/domain/catalog/bearing';

export const DUAL_MOBILITY_HEAD_SIZE = HeadSize.H28;
export const DUAL_MOBILITY_INNER_DIAMETER_SIZE = BearingInnerDiameter.D28;

export type ComponentCatalog = {
    cups: Map<Url, CatalogCup>;
    liners: Map<Url, CatalogLiner>;
    bearings: Map<Url, CatalogBearing>;
    heads: Map<Url, CatalogHead>;
    stems: Map<Url, CatalogStem>;
};

export function findCatalogLiner(
    catalog: ComponentCatalog,
    size: LinerSize,
    headSize: HeadSize | HeadSizeString,
    type: LinerType = LinerType.NEUTRAL,
): CatalogLiner {
    let headSizeNum: HeadSize;
    if (typeof headSize === 'string') {
        headSizeNum = headSizeFromString(headSize);
    } else {
        headSizeNum = headSize;
    }
    return findComponent(
        catalog.liners,
        `liner with size ${size}, type ${type} and head-size ${headSizeNum}`,
        liner => liner.size === size && liner.headSize === headSizeNum && liner.type === type,
    );
}

export function findBearing(
    catalog: ComponentCatalog,
    linerSize: LinerSize,
    headSize: BearingInnerDiameter,
): CatalogBearing {
    return findComponent(
        catalog.bearings,
        `bearing with liner size ${linerSize} and head size ${headSize}`,
        bearing => bearing.linerSize === linerSize && bearing.innerDiameter === headSize,
    );
}

export function getCatalogComponent<T>(components: Map<Url, T>, url: Url): T {
    const result = components.get(url);
    if (result !== undefined) {
        return result;
    }
    throw Error(`Failed to get component matching uri ${url}`);
}

export function findHead(
    catalog: ComponentCatalog,
    system: HeadSystem,
    size: HeadSize | HeadSizeString,
    offset: HeadOffset,
): CatalogHead {
    let headSizeStr: HeadSizeString;
    if (typeof size === 'number') {
        headSizeStr = headSizeString(size);
    } else {
        headSizeStr = size;
    }

    return findComponent(
        catalog.heads,
        `head with system ${system}, size ${headSizeStr}, and offset ${offset}`,
        head => head.system === system && head.size === headSizeStr && head.offset === offset,
    );
}

function findComponent<T>(
    components: Map<Url, T>,
    description: string,
    predicate: (component: T) => boolean,
): T {
    const results = filter(predicate, Array.from(components.values()));
    if (results.length === 1) {
        return results[0];
    }
    if (results.length == 0) {
        throw Error(`Failed to find ${description}`);
    } else {
        throw Error(`Found multiple components matching ${description}`);
    }
}

